import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Space, Tag, Button, Modal, Form, Input, message } from 'antd';
import { YoutubeOutlined, LinkOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api, { formatApiError } from '../utils/api';

// Shown at the top of every page:
// 1) Bainsla Music staff: new First Time Use videos whose details must be filled and made Public.
// 2) Staff on the notify list: songs uploaded to a YouTube channel in the last 7 days.
export default function VideoTrackerBanner() {
  const [items, setItems] = useState([]);
  const [queue, setQueue] = useState([]);
  const [pubRow, setPubRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchFeed = useCallback(async () => {
    try {
      const res = await api.get('/video-tracker/feed');
      setItems(res.data || []);
    } catch (err) { /* table may not exist / no access */ }
    try {
      const res = await api.get('/video-tracker/publish-queue');
      setQueue(Array.isArray(res.data) ? res.data : []);
    } catch (err) { /* no access */ }
  }, []);

  useEffect(() => {
    fetchFeed();
    const t = setInterval(() => { if (!document.hidden) fetchFeed(); }, 120000);
    return () => clearInterval(t);
  }, [fetchFeed]);

  const openPublish = (row) => {
    setPubRow(row);
    form.setFieldsValue({ video_title: row.video_title || row.song_title || '', youtube_link: row.youtube_link || '', note: '' });
  };

  const submitPublish = async () => {
    try {
      const vals = await form.validateFields();
      setSaving(true);
      await api.post(`/video-tracker/${pubRow.id}/publish`, vals);
      message.success('Details save ho gayi — video Public mark ho gaya');
      setPubRow(null);
      fetchFeed();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(formatApiError(err, 'Save nahi hua'));
    } finally { setSaving(false); }
  };

  return (
    <>
      {queue.length > 0 && (
        <Alert
          type="warning" showIcon icon={<YoutubeOutlined />}
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={<b>Naye song upload hue — details bharo aur Public karo ({queue.length})</b>}
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              <span style={{ color: '#8c8c8c' }}>
                Tarika: YouTube Studio me video kholo → title, description, tags bharo → Visibility "Public" karo → yahan "Details bharo & Public" dabake title + link save karo.
              </span>
              {queue.map(it => (
                <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {it.first_time_code && <Tag color="purple">{it.first_time_code}</Tag>}
                  <span style={{ fontWeight: 600 }}>{it.song_title}</span>
                  {it.singer && <Tag>{it.singer}</Tag>}
                  {it.channel_name && <Tag color="red">{it.channel_name}</Tag>}
                  {it.first_time_at && <Tag color="blue">{dayjs(it.first_time_at).format('DD MMM')}</Tag>}
                  {it.youtube_link && <a href={it.youtube_link} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a>}
                  <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => openPublish(it)}>Details bharo & Public</Button>
                </div>
              ))}
            </Space>
          }
        />
      )}
      {items.length > 0 && (
        <Alert
          type="success" showIcon icon={<YoutubeOutlined />}
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={<b>Naye Songs Upload Hue (last 7 days) — {items.length}</b>}
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              {items.map(it => (
                <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{it.song_title}</span>
                  {it.channel_name && <Tag color="red">{it.channel_name}</Tag>}
                  {it.uploaded_at && <Tag color="blue">{dayjs(it.uploaded_at).format('DD MMM')}</Tag>}
                  {it.youtube_link && (
                    <a href={it.youtube_link} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a>
                  )}
                </div>
              ))}
            </Space>
          }
        />
      )}
      <Modal
        title={<span><YoutubeOutlined style={{ color: '#f00' }} /> Details bharo & Public — {pubRow?.song_title}</span>}
        open={!!pubRow} onOk={submitPublish} onCancel={() => setPubRow(null)} okText="Public ho gaya — Save" confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="video_title" label="YouTube Video Title" rules={[{ required: true, message: 'Video title daalo' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="youtube_link" label="YouTube Link" rules={[{ required: true, message: 'YouTube link daalo' }]}>
            <Input placeholder="https://youtu.be/..." />
          </Form.Item>
          <Form.Item name="note" label="Note (optional)">
            <Input placeholder="e.g. tags/description daal diye" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
